import { useState } from 'react';
import { toast } from 'sonner';
import { LinkSimple, Copy, Lightning } from '@phosphor-icons/react';
import api from '../lib/api';

/**
 * Admin-only widget: mint a one-time, 15-minute magic login link for any
 * account — built for handing access to AI agents (Lindy) and VAs without
 * them fighting the login form. Links live 24 hours, work exactly once.
 */
export default function MagicLinkWidget() {
  const [email, setEmail] = useState('chip.salescrunchcrm@gmail.com');
  const [linkType, setLinkType] = useState('single');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (busy || !email.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const standing = linkType === 'standing';
      const { data } = await api.post('/admin/magic-link', {
        email: email.trim(),
        origin_url: window.location.origin,
        duration_days: standing ? 30 : 1,
        reusable: standing,
      });
      setResult(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not generate link');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(result.url).then(
      () => toast.success('Link copied — paste it to Lindy'),
      () => toast.error('Copy failed — select and copy manually'),
    );
  };

  return (
    <div className="border border-slate-200 bg-white rounded-sm mb-4" data-testid="magic-link-widget">
      <div className="px-4 py-3 border-b border-black/5 flex items-center gap-2">
        <LinkSimple size={18} weight="bold" className="text-[#0B5394]" />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[#111827]">
          One-Click Login Link
        </h3>
        <span className="text-[10px] text-[#9CA3AF]">for AI agents &amp; VAs</span>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex gap-2 flex-wrap">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="account email"
            className="flex-1 min-w-[220px] border border-slate-300 rounded px-3 py-1.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0B5394]"
            data-testid="magic-link-email-input"
          />
          <select
            value={linkType}
            onChange={(e) => setLinkType(e.target.value)}
            className="border border-slate-300 rounded px-2 py-1.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0B5394]"
            data-testid="magic-link-type-select"
          >
            <option value="single">Single use · 24 h</option>
            <option value="standing">Standing link · 30 days · reusable (Lindy)</option>
          </select>
          <button
            type="button"
            onClick={generate}
            disabled={busy}
            className="bg-[#F39C12] hover:bg-[#E08C00] disabled:opacity-60 text-black text-xs font-bold px-4 py-1.5 rounded flex items-center gap-1.5"
            data-testid="magic-link-generate-btn"
          >
            <Lightning size={12} weight="fill" />
            {busy ? 'Generating…' : 'Generate link'}
          </button>
        </div>

        {result && (
          <div className="bg-amber-50 border border-[#F39C12]/40 rounded p-3" data-testid="magic-link-result">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#92610a] mb-1">
              Logs into {result.email} ·{' '}
              {result.reusable
                ? `REUSABLE for ${Math.round(result.expires_in_minutes / 1440)} days — Lindy can keep this one`
                : `expires in ${Math.round(result.expires_in_minutes / 60)} h · works ONCE`}
            </div>
            <div className="flex gap-2 items-center">
              <code className="flex-1 text-[10px] bg-white border border-slate-200 rounded px-2 py-1.5 break-all select-all" data-testid="magic-link-url">
                {result.url}
              </code>
              <button
                type="button"
                onClick={copyLink}
                className="bg-[#0B5394] hover:bg-[#094180] text-white text-[10px] font-bold px-3 py-1.5 rounded flex items-center gap-1 whitespace-nowrap"
                data-testid="magic-link-copy-btn"
              >
                <Copy size={11} weight="bold" /> Copy
              </button>
            </div>
            <p className="text-[10px] text-[#6B7280] mt-2">
              Send this to Lindy — she just opens it and she&apos;s logged in. No form, no password.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
